import type { components } from '@/api/schema'
import { findResource, type Resource } from '@/api/resources'

type Contact = components['schemas']['ContactResource']
type Company = components['schemas']['CompanyResource']

/**
 * THE deal-customer derivation — every surface that asks "who is this job for?"
 * comes through here.
 *
 * ⚠ A job's customer is its OWN FROZEN STAMP (`jobs.contact_id` / `company_id`),
 * never `service_location.contact`. The location is the durable anchor and its
 * customer is MUTABLE: reading the deal's customer through the address meant a
 * turnover silently rewrote who every past job was for — the invisible-jobs bug.
 * Nine call sites used to spell that chain by hand; they all read this now, so
 * the board and the desk can never disagree about a name again.
 *
 * TWO INDEPENDENT SLOTS: `company` = who the deal is with (the payer),
 * `contact` = who is on site (the person — the phone, the SMS target). B2B jobs
 * carry both.
 *
 * Requires `include: { contact: true, company: true }` at the JOB level (nested
 * as `job.contact` / `job.company` wherever the job is itself sideloaded).
 */
export const jobContact = (
  job: Resource<'job'> | null | undefined,
  included: Resource[] | undefined
): Resource<'contact'> | null =>
  findResource(included ?? [], job?.relationships?.contact?.data?.id, 'contact')

export const jobCompany = (
  job: Resource<'job'> | null | undefined,
  included: Resource[] | undefined
): Resource<'company'> | null =>
  findResource(included ?? [], job?.relationships?.company?.data?.id, 'company')

/**
 * The customer's display name. Served by the API (`Job#customer_name`,
 * company-first) — this is a thin reader, NOT a second implementation: the
 * server owns the chain so the two can't disagree.
 */
export const customerName = (job: Resource<'job'> | null | undefined): string | null =>
  job?.attributes.customer_name ?? null

/**
 * The label a row shows for a job: the customer, else the job's own title.
 * A customerless job is legitimate (an import that carried no name), and the
 * title is what the dispatcher wrote — never render an empty cell.
 */
export const customerLabel = (job: Resource<'job'> | null | undefined): string | null =>
  customerName(job) ?? job?.attributes.title ?? null

/**
 * WHO DO WE CALL about this deal. The person's cell first, then their landline,
 * then the company's line — a call reaches a human either way, so the fallback
 * chain is a feature here. ⚠ This is the CALL target and nothing else: passing it
 * to an SMS would text a landline, which fails SILENTLY. Use `jobSmsPhone`.
 *
 * Two ways in, ONE rule: from the job + the graph, or from resources the caller
 * already resolved — the board indexes its whole sideload once and must not
 * re-scan it per stop.
 */
export const reachableNumber = (
  contact: Contact | null | undefined,
  company: Company | null | undefined
): string | null => contactCallNumber(contact) ?? company?.attributes.phone ?? null

export const jobPhone = (
  job: Resource<'job'> | null | undefined,
  included: Resource[] | undefined
): string | null => reachableNumber(jobContact(job, included), jobCompany(job, included))

/**
 * WHO CAN WE TEXT about this deal: `contact.cell_phone`, and nothing else.
 *
 * ⚠ NO fallback — not to the contact's `phone`, not to the company's line. A text
 * to a landline is delivered nowhere and reports nothing, so a fallback would
 * manufacture exactly the silent failure the separate column exists to prevent.
 * And a confirmation sent to a company's reception is not the promise we made.
 *
 * `null` is a real answer: « aucun contact SMS » — the dispatcher calls instead.
 */
export const jobSmsPhone = (
  job: Resource<'job'> | null | undefined,
  included: Resource[] | undefined
): string | null => jobContact(job, included)?.attributes.cell_phone ?? null

/**
 * WHAT NUMBER DO WE DIAL for a person, cell first.
 *
 * ⭐ Every caller that used to read `contact.phone` directly must come through
 * here now, and this is not cosmetic: a customer captured through the new intake
 * block may have given only a CELL, so `phone` is null and a call button gated on
 * it renders disabled next to a perfectly reachable person. The split made
 * "the contact's number" a derivation rather than a column.
 */
export const contactCallNumber = (contact: Contact | null | undefined): string | null =>
  contact?.attributes.cell_phone || contact?.attributes.phone || null

/** Can this deal be texted at all? The question every comms surface asks first. */
export const jobIsTextable = (
  job: Resource<'job'> | null | undefined,
  included: Resource[] | undefined
): boolean => !!jobSmsPhone(job, included)

// ONE customer's identity from the two slots — both together, because a B2B deal
// carries both and either alone would merge two customers or split one. It is
// what the sales desk groups a person's dossiers on and what every customer disc
// hashes its colour on, so a customer wears one colour wherever she appears.
// Null when neither slot is filled; the caller picks its own fallback.
export const customerKey = (contact?: string | null, company?: string | null): string | null =>
  contact || company ? `c:${contact ?? ''}|o:${company ?? ''}` : null

// ★ THE CALL KEY — who you CALL about a job: the company, else the person,
// else nothing (the caller falls back to the job itself). The server's twin,
// `Job.customer_key_sql`: ONE call per company — a landlord with five
// addresses is one row on the sales desk and one row in the renewal
// campaign, whose page the server folds on this very expression, so the two
// spellings must never drift. `customerKey` above stays the PAIR: the pickers
// and the directory discs need two contacts of one company told apart.
export const customerCallKey = (contact?: string | null, company?: string | null): string | null =>
  company ? `o:${company}` : contact ? `c:${contact}` : null

/**
 * THE PAYER — who « Paiement avant travaux » defaults from: the company when
 * the job has one, else the contact (the server's order, Job#stamp_payment_first,
 * and the customer key's). `paymentFirst` is that customer's DEFAULT for future
 * jobs, never this job's own mode.
 */
export interface Payer {
  kind: 'company' | 'contact'
  id: string
  name: string
  paymentFirst: boolean
}

export function payerOf(
  job: Resource<'job'> | null | undefined,
  included: Resource[] | undefined
): Payer | null {
  const company = jobCompany(job, included)
  if (company) {
    return { kind: 'company', id: company.id, name: company.attributes.company_name ?? '', paymentFirst: company.attributes.payment_first === true }
  }
  const contact = jobContact(job, included)
  if (!contact) return null
  const name = [ contact.attributes.first_name, contact.attributes.last_name ].filter(Boolean).join(' ')
  return { kind: 'contact', id: contact.id, name, paymentFirst: contact.attributes.payment_first === true }
}
